/**
 * 页面缩放控制组件
 * 支持预设档位选择、逐级放大/缩小、重置为推荐比例；选择结果本地持久化
 */
import React, { useMemo, useState } from 'react'
import { Dropdown } from 'antd'
import { ColumnWidthOutlined, MinusOutlined, PlusOutlined, CheckOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useUiScale, UI_SCALE_STEPS } from '@/hooks/useUiScale'

interface UiScaleControlProps {
  className?: string
  variant?: 'default' | 'toolbar'
}

const formatPercent = (scale: number) => `${Math.round(scale * 100)}%`

const UiScaleControl: React.FC<UiScaleControlProps> = ({ className, variant = 'default' }) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('common')
  const {
    scale,
    recommendedScale,
    isRecommended,
    canStepIn,
    canStepOut,
    setScale,
    stepIn,
    stepOut,
    resetToRecommended,
  } = useUiScale()

  const overlayStyle = { zIndex: 1400 }

  const dropdownContent = useMemo(() => (
    <div
      style={{
        width: 280,
        maxWidth: 'min(280px, calc(100vw - 32px))',
        padding: 16,
        borderRadius: 20,
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-card) 98%, #ffffff 2%), color-mix(in srgb, var(--color-card) 94%, var(--color-bg-subtle) 6%))',
        border: '1px solid color-mix(in srgb, var(--color-border) 88%, transparent)',
        boxShadow: '0 28px 60px color-mix(in srgb, #0f172a 18%, transparent)',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>
          {t('uiScale.title')}
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary)' }}>
          {formatPercent(scale)}
        </span>
      </div>

      {/* 逐级调节 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '8px 10px',
          marginBottom: 14,
          borderRadius: 14,
          border: '1px solid color-mix(in srgb, var(--color-border) 82%, transparent)',
          background: 'color-mix(in srgb, var(--color-bg-subtle) 60%, transparent)',
        }}
      >
        <button
          type="button"
          aria-label={t('uiScale.zoomOut')}
          disabled={!canStepOut}
          onClick={stepOut}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: '1px solid color-mix(in srgb, var(--color-border) 84%, transparent)',
            background: 'var(--color-card)',
            color: 'var(--color-text-secondary)',
            cursor: canStepOut ? 'pointer' : 'not-allowed',
            opacity: canStepOut ? 1 : 0.4,
          }}
        >
          <MinusOutlined style={{ fontSize: 12 }} />
        </button>

        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text)', minWidth: 48, textAlign: 'center' }}>
          {formatPercent(scale)}
        </span>

        <button
          type="button"
          aria-label={t('uiScale.zoomIn')}
          disabled={!canStepIn}
          onClick={stepIn}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: '1px solid color-mix(in srgb, var(--color-border) 84%, transparent)',
            background: 'var(--color-card)',
            color: 'var(--color-text-secondary)',
            cursor: canStepIn ? 'pointer' : 'not-allowed',
            opacity: canStepIn ? 1 : 0.4,
          }}
        >
          <PlusOutlined style={{ fontSize: 12 }} />
        </button>
      </div>

      {/* 预设档位 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, marginBottom: 12 }}>
        {UI_SCALE_STEPS.map((step) => {
          const selected = Math.abs(step - scale) < 1e-6
          const isRecommendedStep = Math.abs(step - recommendedScale) < 1e-6

          return (
            <button
              key={step}
              type="button"
              aria-pressed={selected}
              title={isRecommendedStep ? t('uiScale.recommendedShort') : undefined}
              onClick={() => setScale(step)}
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                minWidth: 0,
                padding: '5px 8px',
                borderRadius: 999,
                border: selected
                  ? '1.5px solid var(--color-primary)'
                  : '1px solid color-mix(in srgb, var(--color-border) 84%, transparent)',
                background: selected
                  ? 'color-mix(in srgb, var(--color-primary) 12%, var(--color-card) 88%)'
                  : 'var(--color-card)',
                color: selected ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                fontSize: 12,
                fontWeight: selected ? 700 : 500,
                cursor: 'pointer',
              }}
            >
              {selected && <CheckOutlined style={{ fontSize: 10, flex: '0 0 auto' }} />}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {formatPercent(step)}
              </span>
              {isRecommendedStep && (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    top: -7,
                    right: -6,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 14,
                    padding: '0 4px',
                    borderRadius: 999,
                    background: 'var(--color-primary)',
                    color: '#fff',
                    fontSize: 9,
                    fontWeight: 700,
                    lineHeight: '14px',
                    boxShadow: '0 0 0 2px var(--color-card)',
                  }}
                >
                  {t('uiScale.recommendedBadge')}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* 重置为推荐值 */}
      <button
        type="button"
        disabled={isRecommended}
        onClick={resetToRecommended}
        style={{
          width: '100%',
          padding: '7px 0',
          borderRadius: 12,
          border: '1px dashed color-mix(in srgb, var(--color-border) 90%, transparent)',
          background: 'transparent',
          color: isRecommended ? 'var(--color-text-secondary)' : 'var(--color-primary)',
          fontSize: 12,
          fontWeight: 600,
          cursor: isRecommended ? 'default' : 'pointer',
          opacity: isRecommended ? 0.5 : 1,
        }}
      >
        {t('uiScale.resetToRecommended', { value: formatPercent(recommendedScale) })}
      </button>
    </div>
  ), [scale, recommendedScale, isRecommended, canStepIn, canStepOut, setScale, stepIn, stepOut, resetToRecommended, t])

  const triggerLabel = `${t('uiScale.title')} ${formatPercent(scale)}`

  const buttonNode = variant === 'toolbar' ? (
    <button
      type="button"
      className={className}
      data-open={open ? 'true' : 'false'}
      aria-label={triggerLabel}
      title={triggerLabel}
    >
      <ColumnWidthOutlined style={{ fontSize: 18 }} />
    </button>
  ) : (
    <button
      type="button"
      className={className}
      data-open={open ? 'true' : 'false'}
      aria-label={triggerLabel}
      title={triggerLabel}
      style={className ? undefined : {
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        padding: 0,
        borderRadius: '999px',
        border: '1px solid var(--color-border)',
        background: 'var(--color-card)',
        color: 'var(--color-text-secondary)',
      }}
    >
      <ColumnWidthOutlined style={{ fontSize: 16 }} />
    </button>
  )

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      trigger={['click']}
      overlayStyle={overlayStyle}
      dropdownRender={() => dropdownContent}
    >
      {buttonNode}
    </Dropdown>
  )
}

export default UiScaleControl
